import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Alert, Button } from "reactstrap";
import { capitalizeFirstLetter } from "../scan/scanConfig";
import { useScanPipeline } from "../scan/useScanPipeline";
import { patchClaim, postClaim } from "../actions";
import ScanStepLayout from "./ScanStepLayout";

const WAITING_SECONDS = 7;

export default function LabelOCR({ title, side, prevStep, nextStep }) {
    const post = useSelector(state => state.addclaim.post);
    const dispatch = useDispatch();
    const webcamRef = useRef(null);

    const [selectedDeviceId, setSelectedDeviceId] = useState(() =>
        localStorage.getItem('selectedCameraDeviceId') || null
    );
    const [devices, setDevices] = useState([]);
    const [countdown, setCountdown] = useState(WAITING_SECONDS * 2);
    const countdownTimerRef = useRef(null);
    const [clickCount, setClickCount] = useState(0);
    const isToggled = clickCount % 4 === 3;

    const {
        canvasRef,
        regex,
        camera_results,
        jobQueue,
        processedImage,
        resetRegex,
        keep_trying_or_click_image,
        labels,
    } = useScanPipeline({ side, meta: undefined, webcamRef });

    const selectedDeviceIdExists = devices.find(d => d.deviceId === selectedDeviceId);
    const videoConstraints = useMemo(() => ({
        facingMode: "environment",
        focusMode: "continuous",
        width: 960,
        height: 720,
        ...(selectedDeviceIdExists && { deviceId: { exact: selectedDeviceId } }),
    }), [selectedDeviceIdExists, selectedDeviceId]);

    useEffect(() => {
        countdownTimerRef.current = setInterval(() => {
            setCountdown(prev => Math.max(prev - 1, 0));
        }, 500);
        return () => clearInterval(countdownTimerRef.current);
    }, []);

    useEffect(() => {
        if (countdown <= 0 && !post.images?.[side]) {
            dispatch(postClaim({ ...post, images: { ...post.images, [side]: { scanned: true } } }));
        }
    }, [countdown, side, post, dispatch]);

    const goBack = useCallback(() => dispatch(patchClaim({ step: prevStep })), [dispatch, prevStep]);
    const goNext = useCallback(() => dispatch(patchClaim({ step: nextStep })), [dispatch, nextStep]);

    const onCameraError = useCallback((error) => console.error("onCameraError", error), []);

    const onUserMedia = useCallback((media) => {
        const videoTracks = media.getVideoTracks();
        const track = selectedDeviceId
            ? videoTracks.find(t => t.getSettings()?.deviceId === selectedDeviceId) || videoTracks[0]
            : videoTracks[0];
        if (track) {
            navigator.mediaDevices.enumerateDevices().then(mediaDevices =>
                setDevices(mediaDevices.filter(d => d.kind === "videoinput"))
            );
        }
    }, [selectedDeviceId]);

    const handleClick = () => setClickCount(prev => (prev === 3 ? 0 : prev + 1));

    const instructions = useMemo(() => {
        const selectDevice = devices.length > 1 ? <span> If the camera view is not clear or distorted, please select another camera. </span> : null;
        if (keep_trying_or_click_image) {
            return <Alert color="warning" className="mb-3">Keep trying to scan the label. {selectDevice}</Alert>;
        }
        return <Alert color="info" className="mb-3">Please point the camera directly at the label and try to scan the needed information. {selectDevice}</Alert>;
    }, [keep_trying_or_click_image, devices.length]);

    const hasValidImage = Boolean(post.images?.[side]);
    const results = regex.map((i, index) => (
        <div key={index} className="pt-2">
            {isToggled && <i>{String(i.regex)}<br /></i>}
            <div onClick={handleClick}><b>{capitalizeFirstLetter(labels?.[i.field]?.label || i.field)}</b></div>
            {i.result && (
                <span className="text-success" onClick={() => resetRegex(i)}>
                    <Button color="light">{i.result} <span className="text-danger mx-1"><FontAwesomeIcon icon={["far", "times"]} /></span></Button>
                </span>
            )}
        </div>
    ));

    return (
        <ScanStepLayout
            title={title}
            instructions={instructions}
            side={side}
            itemNumber="label"
            devices={devices}
            selectedDeviceId={selectedDeviceId}
            onDeviceChange={setSelectedDeviceId}
            webcamRef={webcamRef}
            videoConstraints={videoConstraints}
            onCameraError={onCameraError}
            onUserMedia={onUserMedia}
            processedImage={processedImage}
            isToggled={isToggled}
            countdown={countdown}
            showProgress
            waitingSeconds={WAITING_SECONDS}
            onOverlayClick={null}
            cordCylinder={false}
            zoomSlider={null}
            regexPickers={null}
            results={results}
            jobQueue={jobQueue}
            camera_results={camera_results}
            canvasRef={canvasRef}
            goBack={goBack}
            goNext={goNext}
            nextDisabled={!hasValidImage}
            nextLoading={false}
            traceback={post.traceback}
        />
    );
}
